import { useQuery } from '@tanstack/react-query';

import { useApiClient } from '@@common';

import { ContextCodeBaseTypeModel, ContextCodeClient } from '../../../api/api';

/**
 * Shared hook for code base information with React Query caching.
 * Use this instead of directly calling getCodeBaseDomainInfo or getCodeBaseApplicationInfo
 * to benefit from automatic caching across views.
 *
 * @param contextId - The context ID
 * @param type - Either 'domain' or 'application'
 * @returns React Query result with codeBaseInfo data
 */
export const useCodeBaseInfo = (contextId: string, type: 'domain' | 'application') => {
	const client = useApiClient(ContextCodeClient);

	return useQuery({
		queryKey: ['codeBase', contextId, type],
		queryFn: async () => {
			const codeBaseType = type === 'domain' ? ContextCodeBaseTypeModel.Domain : ContextCodeBaseTypeModel.Application;
			return await client.getCodeBaseInformation(contextId, codeBaseType);
		},
		enabled: !!contextId,
		staleTime: 10000, // Cache for 10 seconds (code base state can change frequently)
		gcTime: 2 * 60 * 1000, // Keep in cache for 2 minutes
		refetchOnWindowFocus: false, // Don't refetch when switching browser tabs
	});
};
